/**
 * Validation utilities for auth forms
 */
import { VALIDATION_PATTERNS, ERROR_MESSAGES, INPUT_TYPES } from '../types';

/**
 * Detect if input is mobile or email
 * @param {string} value - Input value
 * @returns {string} INPUT_TYPES.MOBILE or INPUT_TYPES.EMAIL
 */
export function detectInputType(value) {
  if (!value) return null;
  // If contains @, it's an email (even if it starts with digits)
  if (value.includes('@')) {
    return INPUT_TYPES.EMAIL;
  }
  // If starts with digit and has no @, it's a mobile number
  if (/^\d/.test(value)) {
    return INPUT_TYPES.MOBILE;
  }
  return INPUT_TYPES.EMAIL;
}

/**
 * Validate mobile number
 * @param {string} mobile - Mobile number
 * @returns {{ isValid: boolean, error: string }}
 */
export function validateMobile(mobile) {
  if (!mobile || mobile.length === 0) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_MOBILE };
  }
  
  if (!/^[6-9]/.test(mobile)) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_MOBILE_START };
  }
  
  if (mobile.length !== 10) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_MOBILE };
  }
  
  if (!VALIDATION_PATTERNS.MOBILE.test(mobile)) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_MOBILE };
  }
  
  return { isValid: true, error: '' };
}

/**
 * Validate email
 * @param {string} email - Email address
 * @returns {{ isValid: boolean, error: string }}
 */
export function validateEmail(email) {
  if (!email || email.length === 0) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_EMAIL };
  }
  
  if (!VALIDATION_PATTERNS.EMAIL.test(email)) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_EMAIL };
  }
  
  return { isValid: true, error: '' };
}

/**
 * Validate mobile or email input
 * @param {string} value - Input value
 * @returns {{ isValid: boolean, error: string, type: string }}
 */
export function validateMobileOrEmail(value) {
  const type = detectInputType(value);
  
  if (!value) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_MOBILE, type: null };
  }
  
  if (type === INPUT_TYPES.MOBILE) {
    const result = validateMobile(value);
    return { ...result, type };
  }
  
  const result = validateEmail(value);
  return { ...result, type };
}

/**
 * Validate OTP
 * @param {string} otp - OTP value
 * @returns {{ isValid: boolean, error: string }}
 */
export function validateOtp(otp) {
  if (!otp || otp.length === 0) {
    return { isValid: false, error: ERROR_MESSAGES.REQUIRED_OTP };
  }
  
  if (!VALIDATION_PATTERNS.OTP.test(otp)) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_OTP };
  }
  
  return { isValid: true, error: '' };
}

/**
 * Validate password with detailed requirements
 * @param {string} password - Password value
 * @returns {{ isValid: boolean, error: string, details: Object }}
 */
export function validatePassword(password) {
  const details = {
    minLength: password.length >= VALIDATION_PATTERNS.PASSWORD.MIN_LENGTH,
    specialChar: VALIDATION_PATTERNS.PASSWORD.SPECIAL_CHAR.test(password),
    number: VALIDATION_PATTERNS.PASSWORD.NUMBER.test(password),
    uppercase: VALIDATION_PATTERNS.PASSWORD.UPPERCASE.test(password),
  };
  
  const isValid = Object.values(details).every(Boolean);
  
  let error = '';
  if (!details.minLength) error = ERROR_MESSAGES.PASSWORD_MIN_LENGTH;
  else if (!details.specialChar) error = ERROR_MESSAGES.PASSWORD_SPECIAL_CHAR;
  else if (!details.number) error = ERROR_MESSAGES.PASSWORD_NUMBER;
  else if (!details.uppercase) error = ERROR_MESSAGES.PASSWORD_UPPERCASE;
  
  return { isValid, error, details };
}

/**
 * Validate name (first/last name)
 * @param {string} name - Name value
 * @param {string} fieldName - Field name for error message
 * @returns {{ isValid: boolean, error: string }}
 */
export function validateName(name, fieldName = 'Name') {
  const trimmed = name?.trim();
  
  if (!trimmed) {
    return { isValid: false, error: `Please enter your ${fieldName.toLowerCase()}` };
  }
  
  if (!VALIDATION_PATTERNS.NAME.test(trimmed)) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_NAME };
  }
  
  return { isValid: true, error: '' };
}

/**
 * Sanitize mobile input (remove non-digits, limit to 10)
 * @param {string} value - Input value
 * @returns {string} Sanitized value
 */
export function sanitizeMobileInput(value) {
  return value.replace(/\D/g, '').slice(0, 10);
}

/**
 * Sanitize OTP input (remove non-digits, limit to 6)
 * @param {string} value - Input value
 * @returns {string} Sanitized value
 */
export function sanitizeOtpInput(value) {
  return value.replace(/\D/g, '').slice(0, 6);
}
